import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import useIeCheck from 'hooks/useIeCheck';
import Layout from 'components/UI/Layout';
import VariantButton from 'components/UI/Shop/VariantButton';
import Breadcrumbs from 'components/UI/Breadcrumbs';
import ProductImages from 'components/UI/ProductImages';
import CTA from 'components/UI/Cta';
import Link from 'components/UI/Link';
import ProductVto from 'components/UI/VTO/ProductVto';
import { Place } from '@material-ui/icons';
import { useRouter } from 'next/router';

import {
  toSlug,
  getServerSidePropsWithErrors,
  BrandedSiteProps,
  BadRequestError,
  cacheDirective,
  getBrandedSiteData,
  PageDates,
  getRedirect,
  CustomRedirect,
  checkBasicAuth,
  withSiteData,
  notFound,
} from 'lib/routing';
import { Product, Variant, Brand, Category } from 'lib/bigcomm/client';
import { IProductVariantFields, ISiteFields } from 'types/contentful';
import { buildProductDescription, variantColorValue, customFieldValue } from 'lib/product';
import {
  generateSSCookiesAndHeaders,
  search,
  SearchResultWithVariants,
  searchUrl,
} from 'lib/searchspring';
import SearchProductCard from 'components/UI/Search/SearchProductCard';
import cmsClient from 'lib/cms/client';
import useScript from 'hooks/useScript';
import ProductData from 'components/StructuredData/ProductData';
import {
  getBrand,
  getCategoryFromCategoryId,
  getPriceRecordForVariant,
  getProduct,
} from 'lib/bigcomm';

interface ProductDetailProps {
  preview: boolean;
  product: Product;
  productBrand: Brand;
  category: Category;
  variant: Variant;
  variantContent: IProductVariantFields;
  siteData: BrandedSiteProps;
  currentSlug: string;
  title: string;
  relatedProducts: SearchResultWithVariants[];
  frameShape: string;
  color: string;
  publishedVariants: string[];
  pageDates: PageDates;
}

function Section({ header, children }: { header?: string; children: ReactNode }): ReactElement {
  const [open, setOpen] = useState(true);

  return (
    <div className="flex flex-col">
      {header && (
        <summary
          onClick={() => setOpen(!open)}
          className="flex flex-row justify-between items-center font-bold cursor-pointer p-4"
        >
          <div className="flex justify-between items-center md:px-4 border-b border-gray-400 w-full">
            <h2 className="font-heading text-primary-actual font-bold text-xl">{header}</h2>
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 160 160">
              <rect
                className={`transition duration-500 ease-in-out transform ${
                  open ? 'rotate-90' : ''
                }`}
                x="70"
                width="20"
                height="160"
              />
              <rect y="70" width="160" height="20" />
            </svg>
          </div>
        </summary>
      )}

      <Transition
        show={open}
        unmount={false}
        enter="transition ease-out duration-150 origin-top"
        enterFrom="transform opacity-0 scale-y-0"
        enterTo="transform opacity-100 scale-y-100"
        leave="transition ease-in duration-150 origin-top"
        leaveFrom="transform opacity-100 scale-y-100"
        leaveTo="transform opacity-0 scale-y-0"
      >
        <div className="p-4 mb-10">{children}</div>
      </Transition>
    </div>
  );
}

export default function ProductDetail({
  currentSlug,
  title,
  product,
  productBrand,
  category,
  variant,
  variantContent,
  preview,
  siteData,
  relatedProducts,
  color,
  frameShape,
  publishedVariants,
  pageDates,
}: ProductDetailProps): ReactElement {
  const [vtoOpen, setVtoOpen] = useState(false);
  const isIE11 = useIeCheck();
  const material = customFieldValue(product, 'Material');
  const pdRange = customFieldValue(product, 'PD Range');
  const rxRange = customFieldValue(product, 'Rx Range');
  const vtoEnabled = siteData.ecommIds?.fields.enableVto && variantContent.vto && !isIE11;
  const showPricing = siteData.ecommIds?.fields.showPricing;
  const faceShapes = product.custom_fields.filter((item) => item.name === 'Face Shape');

  const router = useRouter();

  const relatedHeading = (): string => {
    const childName = firstChildName(category);
    const childCat = childName.endsWith('s') ? childName.replace('s', "'s") : childName;

    return `Similar ${childCat} ${category.name} To ${brandAndName}`;
  };

  const buildSubHeading = (): string => {
    return color + ' ' + frameShape + (showPricing ? ' | $' + variant.price : '');
  };
  const brandAndName = `${productBrand.name} ${product.name}`;
  const description = `Create a bold identity with the ${productBrand.name} ${frameShape} ${material} ${category.name} in ${color}. Buy online or schedule an appointment at ${siteData.siteName}.`;
  const nextSeoProps = {
    title,
    description,
    openGraph: {
      images: variantContent.primaryFrameImage
        ? [
            {
              url: `https:${variantContent.primaryFrameImage.fields.file.url}?fit=fill&f=center&h=630&w=1200`,
              width: 1200,
              height: 630,
              alt:
                variantContent.primaryFrameImage.fields.description ||
                variantContent.primaryFrameImage.fields.title,
            },
          ]
        : [],
    },
  };
  const colorOptions = (variant: Variant): string => variantColorValue(variant) || '';

  const triggerIntelliSuggest = (): void => {
    // https://searchspring.zendesk.com/hc/en-us/articles/201185129-Adding-IntelliSuggest-Tracking#productviews
    const productCode = variant.sku;
    window.IntelliSuggest.init({
      siteId: siteData.ecommIds?.fields.searchSpringId as string,
      context: `Product/${productCode}`,
      seed: [productCode],
    });
    // add user id here if customer is logged in
    // IntelliSuggest.setShopperId();
    window.IntelliSuggest.viewItem({ sku: productCode });
  };

  useScript('//cdn.searchspring.net/intellisuggest/is.min.js', false, () =>
    triggerIntelliSuggest()
  );

  const locationInfo = (): Record<string, unknown> => {
    return {
      location: router?.query?.locationName,
      zip: router?.query?.locationZip,
    };
  };

  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'eec.detail',
        ecommerce: {
          detail: {
            actionField: { list: 'Search Results' },
            products: [
              {
                name: product.name,
                id: variant.sku,
                price: variant.price,
                brand: productBrand.name,
                category: `${category.name}>${firstChildName(category)}`,
                variant: variantColorValue(variant),
              },
            ],
          },
        },
      });
    }
  });

  return (
    <>
      {/* Structured Data */}
      <ProductData
        productName={product.name}
        description={description}
        variant={variant}
        variantContent={variantContent}
        brand={productBrand.name}
        material={material || ''}
        color={color}
        siteName={siteData.siteName}
      />

      <Layout
        nextSeoProps={nextSeoProps}
        siteData={siteData}
        preview={preview}
        currentSlug={currentSlug}
        pageDates={pageDates}
      >
        {/* top widget */}
        <Section>
          {locationInfo().location && locationInfo().zip && (
            <div className="bg-primary p-2 mt-4 text-white">
              <div className="flex  items-center">
                <div className="text-highlight-actual">
                  <Place color="inherit" style={{ fontSize: 45 }} />
                </div>
                <h3 className="font-bold text-xl text-white">
                  Available for try-on at {locationInfo().location} ({locationInfo().zip})
                </h3>
              </div>
            </div>
          )}

          <Breadcrumbs productName={product.name} category={category.name} />

          <div className="mt-4 flex flex-col md:flex-row flex-wrap">
            <div className="w-full md:w-3/5 md:ml-4">
              <ProductImages {...variantContent} />
            </div>

            <div className="md:ml-16 mt-6 flex-1" style={{ minWidth: 275 }}>
              <div className="flex flex-col-reverse md:flex-col">
                <div>
                  <h3 className="font-semibold text-2xl md:text-3xl">
                    {brandAndName}
                    <span className="block font-normal text-base md:text-xl">
                      {buildSubHeading()}
                    </span>
                  </h3>
                </div>

                <ul
                  className="mb-8 md:mb-0 md:mt-2 flex flex-wrap justify-center md:justify-start"
                  aria-label="color options"
                >
                  {product.variants
                    .filter((variant) => publishedVariants.includes(variant.sku))
                    .sort((a, b) => (colorOptions(a) < colorOptions(b) ? -1 : 1))
                    .map((variant) => (
                      <VariantButton
                        uid={product.id}
                        key={variant.sku}
                        sku={variant.sku}
                        name={product.name}
                        color={colorOptions(variant)}
                      />
                    ))}
                </ul>
              </div>

              {vtoEnabled && (
                <CTA
                  text={vtoOpen ? 'Hide Virtual Try-On' : 'Virtual Try-On'}
                  colorScheme="Primary"
                  slug=""
                  classNames="mt-8"
                  onClick={() => {
                    setVtoOpen(!vtoOpen);
                  }}
                />
              )}

              <div className="hidden md:block mt-2">
                Need a new prescription?{' '}
                <Link slug="locations">
                  <a className="text-tertiary-actual">Schedule Appointment</a>
                </Link>
              </div>
            </div>
          </div>
        </Section>

        {vtoEnabled && (
          <Transition
            show={vtoOpen}
            unmount={true}
            enter="transition ease-out duration-150 origin-top"
            enterFrom="transform opacity-0 scale-y-0"
            enterTo="transform opacity-100 scale-y-100"
            leave="transition ease-in duration-150 origin-top"
            leaveFrom="transform opacity-100 scale-y-100"
            leaveTo="transform opacity-0 scale-y-0"
          >
            <ProductVto
              glassesId={variantContent.upc}
              onClose={() => setVtoOpen(false)}
              siteName={siteData.siteName}
            />
          </Transition>
        )}

        {/* description */}
        <Section header={`About ${brandAndName}`}>
          <div className="lg:px-4 leading-10">
            {buildProductDescription(
              product,
              variant,
              siteData.siteName,
              productBrand.name,
              category
            )}
          </div>
        </Section>

        {/* details */}
        <Section header={`${product.name} Details`}>
          <div className="flex flex-col md:flex-row md:px-4">
            <div className="w-full md:w-1/2 mt-5">
              <h3 className="text-lg font-bold border-b-3 border-secondary inline-block">
                Specifications
              </h3>
              <div className="flex flex-col mt-4 leading-10">
                <div>Color: {color || 'N/A'}</div>
                {pdRange && <div>PD Range: {pdRange}</div>}
                {rxRange && <div>Prescription Range: {rxRange}</div>}
                <div>Rim Style: {customFieldValue(product, 'Rim') || 'N/A'}</div>
                <div>Shape: {frameShape || 'N/A'}</div>
                <div>Material: {material || 'N/A'}</div>
                <div>Lens Type: {customFieldValue(product, 'Lens Type') || 'N/A'}</div>
                <div>
                  Available as Progressive/Bifocal:{' '}
                  {customFieldValue(product, 'Progressive/Bifocal Option (Y/N)') || 'N/A'}
                </div>
              </div>
            </div>

            <div className="w-full md:w-1/2 mt-5 leading-10">
              <h3 className="text-lg font-bold border-b-3 border-secondary inline-block">
                Frame Size
              </h3>
              <div className="flex flex-col mt-4">
                <div>Bridge: {variantContent.bridge} mm</div>
                <div>Lens Width: {variantContent.lensWidth} mm</div>
                <div>Lens Height: {variantContent.lensHeight} mm</div>
              </div>
            </div>
          </div>
        </Section>

        {/* Face shape */}
        <div className="mx-4 mb-16">
          <h2 className="font-heading text-primary-actual md:text-xl">
            Face Shape Recommendations for {brandAndName}
          </h2>
          <ul aria-label="face shape recommendations list" className="flex flex-wrap mt-4 md:mt-16">
            {faceShapes.map((shape) => (
              <li
                key={shape.value}
                className="mr-6 md:mr-24 text-center flex flex-col justify-between"
                aria-label={`${shape.value} face shape`}
              >
                <img
                  src={`/images/shapes/${shape.value.toLowerCase()}.svg`}
                  alt={shape.value}
                  className="mb-4 flex-auto h-10 md:h-auto"
                />
                {shape.value}
              </li>
            ))}
          </ul>
        </div>

        {/* related products */}
        {relatedProducts.length > 0 && (
          <div className="mx-4 mb-16">
            <h2 className="font-heading text-primary-actual text-xl md:text-4xl">
              {relatedHeading()}
            </h2>
            <ul className="list-none flex flex-col md:flex-row mt-6">
              {relatedProducts.map((item, i) => (
                <SearchProductCard key={i} {...item} />
              ))}
            </ul>
          </div>
        )}
      </Layout>
    </>
  );
}

export const getServerSideProps = getServerSidePropsWithErrors<ProductDetailProps>(
  async ({ params, res, req, preview, resolvedUrl }) => {
    const client = cmsClient(preview);
    const slug = toSlug(params?.product) || 'shop';
    const slugData = slug.split('-');
    const sku = slugData.pop();
    const productId = slugData.pop();

    const {
      items: [site],
    } = await client.getEntries<ISiteFields>({
      'fields.id': req.headers.host || '',
      content_type: 'site',
      include: 10,
    });

    if (!site) {
      return {
        notFound: true,
      };
    }

    const siteData = getBrandedSiteData(site);

    const productNotFoundResponse = withSiteData(notFound(), siteData);

    if (!siteData.ecommIds) {
      return productNotFoundResponse;
    }

    const isAuthenticated = checkBasicAuth(res, site.fields, req.headers.authorization);

    if (!isAuthenticated) {
      return {
        notFound: true,
      };
    }

    const redirectsEntry = site.fields.redirects;
    const redirects = (redirectsEntry?.fields.redirects as CustomRedirect[]) || [];
    const redirect = getRedirect(resolvedUrl, redirects);

    if (redirect) {
      return {
        redirect: {
          statusCode: 301,
          destination: redirect,
        },
      };
    }

    let relatedProducts;

    const product = await getProduct(productId);

    if (!product) {
      return productNotFoundResponse;
    }

    const contentfulVariants = await client.getEntries<IProductVariantFields>({
      'fields.sku[in]': product.variants.map((variant) => variant.sku).join(','),
      content_type: 'productVariant',
    });

    const variantContent = contentfulVariants.items.find(
      (contentfulVariant) => contentfulVariant.fields.sku === sku
    );

    const variant = product.variants.find((variant) => variant.sku === sku);

    if (!variant || !variantContent) {
      console.log(`no variant data found for sku: ${sku}`);
      return productNotFoundResponse;
    }

    const frameShape = customFieldValue(product, 'Frame Shape') || '';
    const color = variantColorValue(variant) || '';

    const priceRecord = await getPriceRecordForVariant(
      siteData.ecommIds.fields.bigCommerceChannelId,
      variant.id
    );

    if (!priceRecord) {
      return productNotFoundResponse;
    }
    variant.price = priceRecord.price;

    const productBrand = await getBrand(product.brand_id);
    const category = await getCategoryFromCategoryId(product.categories[0]);

    if (!category) {
      return productNotFoundResponse;
    }

    try {
      const siteId = siteData.ecommIds.fields.searchSpringId;
      const url = searchUrl(
        {
          'filter.frame_shape': frameShape,
          'filter.color': color,
          siteId,
        },
        [
          `categories_hierarchy=${category.name}${
            category.children.length > 0 ? `>${category.children[0].name}` : ''
          }`,
        ],
        '',
        'finder',
        4
      );

      const headers = generateSSCookiesAndHeaders(req, res);
      relatedProducts = (await search(url, siteId, headers)).results
        .filter((item) => item.uid !== productId)
        .slice(0, 3); // ensure only 3 sent to frontend - sometimes product is included so need to query 4
    } catch (err) {
      throw new BadRequestError(
        `Invalid related product search request for productId: ${product.id}`
      );
    }

    const title = `${color} ${product.name} ${category.name}`;

    res.setHeader('Cache-Control', cacheDirective(preview));

    return {
      props: {
        preview: preview ? true : false,
        siteData,
        currentSlug: `shop/products/${slug}`,
        title,
        product,
        productBrand,
        category,
        variant,
        variantContent: variantContent.fields,
        relatedProducts,
        frameShape,
        color,
        publishedVariants: contentfulVariants.items.map(
          (contentfulVariant) => contentfulVariant.fields.sku
        ),
        pageDates: {
          datePublished: variantContent.sys.createdAt,
          dateModified: variantContent.sys.updatedAt,
        },
      },
    };
  }
);

function firstChildName(category: Category): string {
  return category.children[0]?.name;
}
